/**
 * Where a market is in its life, at a glance.
 *
 *   LivePulse       a dot that pulses green only while the market is trading AND visible
 *                   to the public; amber while it is on its way; grey once it has ended.
 *   MarketLifecycle the pulse, a stage-by-stage timeline, and the concrete next steps to
 *                   get the market live, public and paid out.
 *
 * Works from whatever the page knows: status is required; visibility, publishedAt,
 * approvedAt, opensAt, closesAt, tradeCount and imageUrl sharpen the advice when present.
 * The same file ships in both portals; `portal` picks whose next steps are shown.
 */

const STAGES = [
  { key: "draft", label: "Draft", statuses: ["DRAFT"] },
  { key: "approval", label: "Approval", statuses: ["PENDING_APPROVAL"] },
  { key: "scheduled", label: "Scheduled", statuses: ["SCHEDULED", "OPENING_AUCTION"] },
  { key: "live", label: "Live", statuses: ["OPEN", "SUSPENDED"] },
  { key: "closed", label: "Closed", statuses: ["CLOSED", "AWAITING_RESULT"] },
  { key: "resolved", label: "Resolved", statuses: ["RESULT_PROPOSED", "RESULT_CONFIRMED", "SETTLING"] },
  { key: "settled", label: "Paid out", statuses: ["SETTLED"] },
];

const ENDED = ["SETTLED", "CANCELLED"];

export function lifecycleState(market) {
  const status = market.status;
  const trading = status === "OPEN";
  const listed = market.visibility === undefined ? true : market.visibility === "PUBLIC";
  const published = market.publishedAt === undefined ? true : Boolean(market.publishedAt);
  if (trading && listed && published) return { tone: "live", label: "Live and public" };
  if (trading) return { tone: "warn", label: listed ? "Trading, not published" : "Trading, not listed publicly" };
  if (status === "SUSPENDED") return { tone: "warn", label: "Paused" };
  if (ENDED.includes(status)) return { tone: "off", label: status === "SETTLED" ? "Paid out" : "Cancelled" };
  if (["CLOSED", "AWAITING_RESULT", "RESULT_PROPOSED", "RESULT_CONFIRMED", "SETTLING"].includes(status)) return { tone: "wait", label: "Closed, awaiting payout" };
  return { tone: "wait", label: "Not live yet" };
}

const DOT = {
  live: "bg-green-500",
  warn: "bg-amber-500",
  wait: "bg-amber-400",
  off: "bg-gray-400",
};

export function LivePulse({ market, showLabel = true, className = "" }) {
  const state = lifecycleState(market);
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} title={state.label}>
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {state.tone === "live" && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75 motion-reduce:animate-none" />}
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${DOT[state.tone]}`} />
      </span>
      {showLabel && <span className="whitespace-nowrap text-xs font-semibold text-navy-700 dark:text-white">{state.label}</span>}
    </span>
  );
}

function when(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString("en-KE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** The next things to do, most important first. Plain sentences, each one actionable. */
export function nextSteps(market, portal) {
  const s = market.status;
  const partner = portal === "partner";
  const steps = [];
  const approver = partner ? "your tenant" : "a second administrator (not the one who created it)";

  if (s === "DRAFT") {
    steps.push(partner ? "Finish the question, rules and close time, then submit it for review." : "Finish the details, then submit it for approval.");
    steps.push("Write the resolution rule so a stranger would judge the result the same way you would.");
  }
  if (s === "PENDING_APPROVAL") {
    steps.push(`Waiting for approval by ${approver}.`);
    if (!partner) steps.push("Approve it from Series & Races, Approval Queue, or from the Approve action on this page.");
    steps.push("Check the resolution source names an exact source and moment; vague rules are the usual reason for rejection.");
  }
  if (s === "SCHEDULED" || s === "OPENING_AUCTION") {
    steps.push(market.opensAt ? `Approved. It opens for trading automatically at ${when(market.opensAt)}.` : "Approved. It opens for trading automatically at its open time.");
  }
  if (["DRAFT", "PENDING_APPROVAL", "SCHEDULED", "OPENING_AUCTION", "OPEN"].includes(s)) {
    if (market.visibility !== undefined && market.visibility !== "PUBLIC") {
      steps.push(`Its visibility is ${market.visibility}, so the public cannot see it. ${partner ? "Ask your tenant to make it public." : "Set visibility to PUBLIC."}`);
    }
    if (market.publishedAt === null && market.approvedAt) {
      steps.push(partner ? "Approved but not published to the storefront yet. Your tenant publishes it." : "Approved but not published: use Publish above to put it on the storefront.");
    }
    if (market.imageUrl === null) steps.push("Add an image. Markets with a picture get noticeably more trades.");
  }
  if (s === "OPEN") {
    if (market.tradeCount === 0) steps.push("No trades yet. Share the market link or embed it on your site (the embed icon on the market page).");
    if (market.closesAt) steps.push(`Trading closes automatically at ${when(market.closesAt)}.`);
  }
  if (s === "SUSPENDED") steps.push(partner ? "Trading is paused. Your tenant can resume it." : "Trading is paused. Resume it when the reason for the pause is cleared.");
  if (s === "CLOSED" || s === "AWAITING_RESULT") {
    steps.push(partner ? "Trading has closed. Your tenant records the result once the source publishes it." : "Record the result in the Resolution Center once the source publishes it.");
  }
  if (s === "RESULT_PROPOSED") steps.push(partner ? "A result has been proposed and is being confirmed." : "A result is proposed: a second operator confirms it, then payouts run automatically.");
  if (s === "RESULT_CONFIRMED" || s === "SETTLING") steps.push("The result is confirmed and correct traders are being paid.");
  if (s === "SETTLED") steps.push("Done. Correct traders have been paid and revenue booked. See Revenue for your share.");
  if (s === "CANCELLED") steps.push("Cancelled. Every trader was refunded in full.");
  return steps;
}

export default function MarketLifecycle({ market, portal = "admin", compact = false }) {
  const status = market.status;
  const cancelled = status === "CANCELLED";
  const current = STAGES.findIndex((stage) => stage.statuses.includes(status));
  const steps = nextSteps(market, portal);

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <LivePulse market={market} />
        <span className="text-xs font-medium text-muted-foreground">{status.replaceAll("_", " ")}</span>
      </div>

      <ol className="mt-4 flex items-start overflow-x-auto pb-1" aria-label="Market lifecycle">
        {STAGES.map((stage, i) => {
          const done = !cancelled && current > i;
          const here = !cancelled && current === i;
          return (
            <li key={stage.key} className="flex min-w-[64px] flex-1 flex-col items-center text-center">
              <div className="flex w-full items-center">
                <span className={`h-0.5 flex-1 ${i === 0 ? "invisible" : done || here ? "bg-brand-500" : "bg-gray-200 dark:bg-white/10"}`} />
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-semibold ${
                    here
                      ? stage.key === "live" && status === "OPEN"
                        ? "border-green-500 bg-green-500 text-white"
                        : "border-brand-500 bg-primary text-primary-foreground"
                      : done
                        ? "border-brand-500 bg-brand-500/15 text-brand-500"
                        : "border-gray-300 text-gray-400 dark:border-white/20"
                  }`}
                  aria-current={here ? "step" : undefined}
                >
                  {done ? "✓" : i + 1}
                </span>
                <span className={`h-0.5 flex-1 ${i === STAGES.length - 1 ? "invisible" : done ? "bg-brand-500" : "bg-gray-200 dark:bg-white/10"}`} />
              </div>
              <span className={`mt-1 text-[11px] ${here ? "font-semibold text-navy-700 dark:text-white" : "text-gray-500"}`}>{stage.label}</span>
            </li>
          );
        })}
      </ol>
      {cancelled && <p className="mt-2 text-xs font-semibold text-red-500">Cancelled: this market left the normal path and every trader was refunded.</p>}

      {!compact && steps.length > 0 && (
        <div className="mt-4 rounded-xl bg-lightPrimary p-3 dark:bg-navy-900">
          <p className="text-xs font-medium text-muted-foreground">Next steps</p>
          <ul className="mt-2 space-y-1.5">
            {steps.map((step) => (
              <li key={step} className="flex gap-2 text-sm text-navy-700 dark:text-gray-200">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
